import { useSupportUserStore } from 'stores/supportUserStore';
import { getUserManagement } from 'api/generated/api/user-management';
import { writeAtsIntegrationsCache } from 'utils/atsCoverage';
import { isSupportLoginEmail } from 'utils/supportUsers';
import { storage, StorageKey } from 'utils/typedStorage';
import { toSameOriginPath } from 'utils/sameOriginPath';

/**
 * @deprecated Use useSupportUserStore().setSupportUser() instead.
 * Kept for backward compatibility with SelectSupportRole.tsx.
 */
export const setIsSupport = () => {
  useSupportUserStore.getState().setSupportUser();
};

/**
 * Whether the stored email points at a different account than the session member.
 * An empty mirror is a first login that applyAuthData populates, not divergence.
 */
export const isStoredEmailDiverged = (
  storedEmail: string | null,
  memberEmail: string
): boolean => Boolean(storedEmail && storedEmail !== memberEmail);

/**
 * Result of setLocalStorageFromEmail:
 * - 'reloading': email changed, a full-page navigation is in flight
 * - 'no-change': storage refreshed, callers may navigate themselves
 * - 'failed': lookup failed or support login, storage is stale
 * - 'stale': another request or session took ownership; callers must do nothing
 */
export type SetUserStorageResult =
  | 'reloading'
  | 'no-change'
  | 'failed'
  | 'stale';

let latestProfileRequest: symbol | null = null;

export const setLocalStorageFromEmail = async (
  email: string,
  redirectTo?: string
): Promise<SetUserStorageResult> => {
  if (isSupportLoginEmail(email)) {
    return 'failed';
  }
  const request = Symbol();
  latestProfileRequest = request;
  const identityKeys = [
    StorageKey.AuthToken,
    StorageKey.Email,
    StorageKey.OrgId,
    StorageKey.UserId,
  ] as const;
  const identity = identityKeys.map((key) => storage.get(key));
  const { isSupportUser, impersonatedEmail } = useSupportUserStore.getState();
  const isStale = (): boolean => {
    const support = useSupportUserStore.getState();
    return (
      latestProfileRequest !== request ||
      identityKeys.some((key, index) => storage.get(key) !== identity[index]) ||
      support.isSupportUser !== isSupportUser ||
      support.impersonatedEmail !== impersonatedEmail
    );
  };

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const payload = { email, timezone };
  let responseAccepted = false;
  try {
    const originalEmail = storage.get(StorageKey.Email);
    const result = await getUserManagement().userManagementGetUserInfo(payload);
    // The lookup can finish after logout, an account switch, or a newer lookup.
    // Guard the whole commit, including cache/impersonation writes and navigation.
    if (isStale()) return 'stale';
    responseAccepted = true;
    const { user } = result;
    const {
      email: userEmail,
      first_name,
      last_name,
      org,
      cal_event_type_id,
      cal_api_key,
    } = user;
    const { ats_integrations } = result;
    storage.set(StorageKey.FirstName, first_name || '');
    storage.set(StorageKey.LastName, last_name || '');
    storage.set(StorageKey.Email, userEmail || '');
    storage.set(StorageKey.OrgName, org.name || '');
    // Org/user IDs come from AuthResponse via the session store. A delayed
    // get_user_info must not overwrite that mirror (stale org after invite
    // or support switch).
    storage.set(StorageKey.CalApiKey, cal_api_key || '');
    storage.set(StorageKey.CalEventTypeId, cal_event_type_id || '');
    // Seed the ATS-integrations mirror through the shared writer so the
    // stored format matches what AtsIntegrationsContext writes after fetch.
    writeAtsIntegrationsCache(ats_integrations);

    // Also update the support user store with the impersonated email
    if (useSupportUserStore.getState().isSupportUser) {
      useSupportUserStore.getState().setImpersonatedEmail(userEmail);
    }

    if (originalEmail !== userEmail) {
      // Full reload re-authenticates with the new impersonation context; assign
      // to a safe target so the support redirect survives the reload.
      const safePath = redirectTo ? toSameOriginPath(redirectTo) : null;
      if (safePath) {
        window.location.assign(safePath);
      } else {
        window.location.reload();
      }
      return 'reloading';
    }
    return 'no-change';
  } catch (error) {
    // swallow-ok: Failed user lookup returns an explicit failed result; support switching shows an error and does not navigate, while session extras leave verified AuthResponse identity authoritative.
    if (!responseAccepted && isStale()) return 'stale';
    console.error('Error setting local storage from email:', error);
    return 'failed';
  }
};
